import { accessor } from 'src/store';
import type {
    NodeCfg,
    NodeKind,
    NodeSocketKindMap,
} from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import type { AppletDesignVersionEntity } from '../../applet.entity';
import {
    type UiNodeSpec,
    nodeKindMap,
} from '../nodes/specs';

/**
 * applies removing a socket from a node.
 *
 * any relevant connections are updated. and the node is rerendered by replacing its cfg object.
 *
 * This updates vuex, so update your node config before calling this.
 */
export function removeNodeSocket<K extends NodeKind> (opts: {
    newNodeCfg: NodeCfg<K>;
    direction: 'input' | 'output';
    /** Method of removing the socket
     * - uniqueIdentifier: remove by key
     * - indexedIdentifier: remove by index and prefix and readjust downstream indexes
     */
    removalMethod: {
        kind: 'uniqueIdentifier';
        value: keyof NodeSocketKindMap[K]['inputs'] | keyof NodeSocketKindMap[K]['outputs'];
    } | {
        index: number;
        prefix: string;
        kind: 'indexedIdentifier';
    };
}): Promise<void> {
    return new Promise(resolve => {
        const {
            // node,
            direction,
            newNodeCfg,
            removalMethod,
        } = opts;

        const itemToRemove = removalMethod.kind === 'uniqueIdentifier' ? removalMethod.value : `${removalMethod.prefix}${removalMethod.index}`;
        if (typeof itemToRemove !== 'string') {
            throw new Error('socketToRemove isnt a string');
        }

        accessor.appletDesign.flowModifiers?.deleteSpecificItem(direction === 'input'
            ? {
                direction,
                nodeId: newNodeCfg.id,
                kind: 'nodeSocket',
                inputKey: itemToRemove,
            }
            : {
                direction,
                nodeId: newNodeCfg.id,
                kind: 'nodeSocket',
                outputKey: itemToRemove,
            }).then(() => {
                // "refetching" the entity data as it may have changed during the `deleteSpecificItem`
                const newEntityData = accessor.entityAsType('appletDesignVersion');
                if (!newEntityData) {
                    resolve();
                    return;
                }

                const newFlow = Object.values(newEntityData.config.flows).find(f => Boolean(f.nodes[newNodeCfg.id]));
                if (!newFlow) {
                    resolve();
                    return;
                }
                const newEntity: AppletDesignVersionEntity = {
                    ...newEntityData,
                    config: {
                        ...newEntityData.config,
                        flows: {
                            ...newEntityData.config.flows,
                            [newFlow.id]: {
                                ...newFlow,
                                nodes: {
                                    ...newFlow.nodes,
                                    [newNodeCfg.id]: {
                                        ...newNodeCfg,
                                    },
                                },
                            },
                        },
                    },
                };

                accessor.setPageEntity({
                    entity: newEntity,
                    type: 'appletDesignVersion',
                });
        
                const nodeKind = newNodeCfg.kind;
                const nodeKindItemMap = nodeKindMap[nodeKind] as UiNodeSpec<typeof nodeKind>;
        
                /**
                 * creating a "callback" that gets called inside of `useFlowEditor`
                 * the callback fires off every time the flow config changes via someting outside of the flow editor (i.e. the call to `setPageEntity` above)
                 * this essentially is the same thing as putting a `.then` on the `setPageEntity` call above but that isn't possible because `setPageEntity` is a vuex action
                 *
                 * The content of the callback sets properties of the rete editor that rely on the changes from `setPageEntity` to be set which is why it needs to be done in this complex, async fashion
                */
                accessor.appletDesign.flowModifiers?.flowUpdateEvent.onSet(async () => {
                    if (removalMethod.kind === 'indexedIdentifier') {
                        if (direction === 'input') {
                            const socketsToAdjust = Object.keys(nodeKindItemMap.inputs(newNodeCfg)).filter(key => key.startsWith(removalMethod.prefix) && parseInt(key.replace(removalMethod.prefix, ''), 10) > removalMethod.index);
                            for (const inputKey of socketsToAdjust) {
                                await accessor.appletDesign.flowModifiers?.updateSocketPort({
                                    direction,
                                    nodeId: newNodeCfg.id,
                                    oldInputKey: inputKey,
                                    newInputKey: `${removalMethod.prefix}${parseInt(inputKey.replace(removalMethod.prefix, ''), 10) - 1}`,
                                });
                            }
                        } else {
                            const socketsToAdjust = Object.keys(nodeKindItemMap.outputs(newNodeCfg)).filter(key => key.startsWith(removalMethod.prefix) && parseInt(key.replace(removalMethod.prefix, ''), 10) > removalMethod.index);
                            for (const outputKey of socketsToAdjust) {
                                await accessor.appletDesign.flowModifiers?.updateSocketPort({
                                    direction,
                                    nodeId: newNodeCfg.id,
                                    oldOutputKey: outputKey,
                                    newOutputKey: `${removalMethod.prefix}${parseInt(outputKey.replace(removalMethod.prefix, ''), 10) - 1}`,
                                });
                            }
                        }
                    }
                    resolve();
                });
            });
    });
}
